import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, OnInit } from '@angular/core';
import { combineLatest, of, Subject, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs';
import { DOCUMENT, NgClass } from '@angular/common';

import { CmsService, ContentSlotData } from '@spartacus/core';

import { DeviceService, ILink, ScrollSpyService } from '@seb/web-design-system/core';
import { SdAnchorBlocComponent } from '@seb/web-design-system/anchor-bloc';

import { AnchorBlocComponentInterface, ComponentWithHeadlineInterface, IStickyAnchorEvent } from '../../../models';
import { AbstractComponent } from '../abstract-component.component';
import { StickyAnchorEventTypeEnum } from '../../../enums';
import { TrackingService } from '../../../services';
import { LinkMapper } from '../../../mappers';

@Component({
  selector: 'wc-anchor-bloc',
  templateUrl: './anchor-bloc.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdAnchorBlocComponent,
    NgClass
  ]
})
export class AnchorBlocComponent extends AbstractComponent<AnchorBlocComponentInterface> implements OnInit {
  links: ILink[];
  isOpened = true;
  isSticky = true;
  isDevice: boolean;
  link$ = new Subject<IStickyAnchorEvent>();
  calls: string[] = [];
  previousMenuState: boolean;
  menuStateChanged: boolean;
  lastExec = 0;
  delay = 1500;
  lastEvent = '';

  constructor(
    private cmsService: CmsService,
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef,
    private scrollSpyService: ScrollSpyService,
    private trackingService: TrackingService,
    @Inject(DOCUMENT) private document: any
  ) {
    super();
  }

  override mandatoryFunction = (cmp: AnchorBlocComponentInterface): boolean => {
    return !!cmp.sections;
  };

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const position = this.document.documentElement.scrollTop || this.document.body.scrollTop;
    this.isOpened = position <= 50;
  }

  ngOnInit(): void {
    this.deviceService.isDevice.pipe(takeUntil(this.destroy$)).subscribe((isDevice) => {
      this.isDevice = isDevice;
      this.cdRef.markForCheck();
    });

    this.scrollSpyService.activeSpyTarget
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((spyTarget) => {
        this.isSticky = spyTarget !== 'footer';
        this.cdRef.markForCheck();
      });

    combineLatest([this.cmsService.getContentSlot('DynamicBody'), this.component$])
      .pipe(
        switchMap(([contentSlot, cmp]: [ContentSlotData, AnchorBlocComponentInterface]) => {
          const componentSlotIds = contentSlot.components?.map((component) => component.uid);
          const componentCmpIds = cmp.sections?.split(' ');
          const componentIds = componentSlotIds?.filter((slotId) => componentCmpIds.includes(slotId));
          return componentIds?.length > 0
            ? combineLatest(componentIds?.map((id) => this.cmsService.getComponentData(id)))
            : of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((components: ComponentWithHeadlineInterface[]) => {
        this.links = components
          .filter((component) => !!component.uid && !!component.headline)
          .map((component) => LinkMapper.toLink(component.uid, component.headline, false));
        this.cdRef.markForCheck();
      });

    this.link$
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => {
          if (event.linkName === this.lastEvent) {
            return false;
          }
          if (StickyAnchorEventTypeEnum.click === event.eventType) {
            return !this.menuStateChanged;
          }
          return false;
        })
      )
      .subscribe((event) => {
        this.lastExec = Date.now();
        this.lastEvent = event.linkName;
        this.trackingService.sendEvent('stickyAnchor', {
          section: event.linkName
        });
      });
  }

  linkClicked(linkName: string): void {
    this.link$.next({
      linkName,
      eventType: StickyAnchorEventTypeEnum.click
    });
  }

  menuHasChanged(menuIsToggled: boolean): void {
    this.menuStateChanged = this.previousMenuState === false && menuIsToggled;
    this.previousMenuState = menuIsToggled;
  }
}
